/**
 * Who is signed in, for the whole app. In `oidc` mode the identity provider decides; in `dev`
 * mode (a laptop against a local API that runs AUTH_MODE=dev) the person types which seeded user
 * to be. Either way the rest of the app sees the same thing: `useSession()` and the Me the API
 * returned, which carries the person's permissions.
 */
import type { Me, Permission } from "@tlcrm/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, use, useCallback, useEffect, useState, type ReactNode } from "react";

import { api, ApiError, configureAuth } from "../api/client";
import { config } from "../config";
import * as oidc from "./oidc";

const DEV_KEY = "tlcrm.dev.email";

function readDevEmail(): string | null {
  try {
    return sessionStorage.getItem(DEV_KEY);
  } catch {
    return null;
  }
}

// The API client asks for a token on every request, outside React; these are what it reads.
let devEmailNow = readDevEmail();
let onExpired: () => void = () => undefined;
configureAuth(
  async () =>
    config.authMode === "dev"
      ? devEmailNow
        ? `dev:${encodeURIComponent(devEmailNow)}`
        : null
      : oidc.idToken(),
  () => onExpired(),
);

type SessionState =
  | { status: "loading" }
  | { status: "signed-out"; reason?: string }
  | { status: "signed-in"; me: Me; can: (p: Permission) => boolean };

interface SessionApi {
  state: SessionState;
  signIn(devEmail?: string): Promise<void>;
  signOut(): Promise<void>;
}

const SessionContext = createContext<SessionApi | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [devEmail, setDevEmail] = useState<string | null>(readDevEmail);
  const [expired, setExpired] = useState(false);
  const hasCredential = config.authMode === "dev" ? devEmail !== null : oidc.hasSession();

  useEffect(() => {
    onExpired = () => setExpired(true);
  }, []);

  const me = useQuery({
    queryKey: ["me", devEmail],
    queryFn: ({ signal }) => api<Me>("/me", { signal }),
    enabled: hasCredential && !expired,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
    staleTime: 5 * 60_000,
  });

  const signIn = useCallback(async (email?: string) => {
    if (config.authMode === "dev") {
      const value = email?.trim().toLowerCase() ?? "";
      if (!value) return;
      try {
        sessionStorage.setItem(DEV_KEY, value);
      } catch {
        // Storage blocked: signed in for this page only.
      }
      devEmailNow = value;
      setExpired(false);
      setDevEmail(value);
      return;
    }
    await oidc.beginSignIn(location.pathname + location.search);
  }, []);

  const signOut = useCallback(async () => {
    queryClient.clear();
    if (config.authMode === "dev") {
      try {
        sessionStorage.removeItem(DEV_KEY);
      } catch {
        // Nothing stored.
      }
      devEmailNow = null;
      setDevEmail(null);
      return;
    }
    await oidc.signOut();
  }, [queryClient]);

  let state: SessionState;
  if (!hasCredential) state = { status: "signed-out" };
  else if (expired)
    state = { status: "signed-out", reason: "Your sign-in expired. Sign in again to carry on." };
  else if (me.isPending) state = { status: "loading" };
  else if (me.isError) {
    state = {
      status: "signed-out",
      reason:
        me.error instanceof ApiError
          ? me.error.message
          : "Can't reach the CRM right now. Check your connection and try again.",
    };
  } else {
    const permissions = new Set(me.data.permissions);
    state = { status: "signed-in", me: me.data, can: (p) => permissions.has(p) };
  }

  return <SessionContext value={{ state, signIn, signOut }}>{children}</SessionContext>;
}

export function useSession(): SessionApi {
  const ctx = use(SessionContext);
  if (!ctx) throw new Error("useSession outside SessionProvider");
  return ctx;
}

/** The signed-in person. Only for components rendered inside the signed-in shell. */
export function useMe(): { me: Me; can: (p: Permission) => boolean } {
  const { state } = useSession();
  if (state.status !== "signed-in") throw new Error("useMe outside the signed-in shell");
  return state;
}
